'use client';

import type { Faro } from '@grafana/faro-web-sdk';

import { isDevelopment } from '@/lib/config';
import { redactFaroItem } from '@/utils/faroRedaction';

// Surface `window.faro` to other modules (SupabaseAuthProvider.setUser, devtools).
// Replaces the `(window as any).faro` pattern from PR #3887.
// (window.__ENV is declared canonically in src/lib/config.ts — TypeScript
// merges Window interface declarations across files.)
declare global {
  interface Window {
    faro?: Faro;
    updateFaroConsent?: (granted: boolean) => void;
  }
}

// In-flight guard: prevents two parallel consent toggles from kicking off
// two dynamic imports + two initializeFaro calls before window.faro is set.
let faroLoading = false;

async function startFaro(): Promise<void> {
  if (typeof window === 'undefined') return;
  // The exported `faro` singleton in @grafana/faro-web-sdk has a default `api`
  // object even before initializeFaro is called — `if (faro.api) return` is
  // NOT a reliable "is already initialized" signal (the canonical
  // faro-nextjs-example uses it but it never lets init proceed against
  // current SDK versions). Use our own `window.faro` marker, set only after
  // a successful init.
  if (window.faro || faroLoading) return;

  const url = process.env['NEXT_PUBLIC_FARO_URL'];
  const appName = process.env['NEXT_PUBLIC_FARO_APP_NAME'];
  if (!url || !appName) return; // no-op when unconfigured

  faroLoading = true;
  try {
    // Dynamic-import the SDK so its ~100KB bundle is never shipped to users
    // who decline analytics consent (or never decide). webpack splits these
    // imports into a separate chunk that only loads when consent flips on.
    const [
      { getWebInstrumentations, initializeFaro },
      { TracingInstrumentation },
    ] = await Promise.all([
      import('@grafana/faro-web-sdk'),
      import('@grafana/faro-web-tracing'),
    ]);

    // initializeFaro auto-assigns window.faro itself (as a read-only property
    // — verified in SDK source). Do NOT re-assign here or it crashes with
    // "Cannot assign to read only property 'faro'". The return value is the
    // same instance now accessible as window.faro.
    initializeFaro({
      url,
      app: {
        name: appName,
        version: window.__ENV?.RELEASE_TAG || '0.0.0-dev',
        environment: isDevelopment ? 'development' : 'production',
      },
      instrumentations: [
        ...getWebInstrumentations(),
        new TracingInstrumentation(),
      ],
      // Faro's first page/session events are sent at init, before a token
      // page's mount effect strips the token (`?token=` on most token pages,
      // `#token=` on the newsletter confirm link): redact it from every item
      // (meta.page.url, event/span attributes) so an emailed credential never
      // reaches Grafana Cloud. Same rule as the PostHog redaction.
      beforeSend: redactFaroItem,
    });
  } catch {
    // swallow — observability must never break the app
  } finally {
    faroLoading = false;
  }
}

/**
 * Register the consent-gate callback at MODULE scope (not in a useEffect or
 * render body). Module-level code runs the moment this 'use client' chunk is
 * evaluated by the browser — before React even starts rendering — so the
 * callback is guaranteed to exist when CookieConsent.tsx fires its
 * onConsent/onChange handlers. This is more resilient than render-scope: if
 * any unrelated component triggers a hydration error (which causes React to
 * abort hydration and re-render from scratch), the consent gate still works
 * because module evaluation already happened.
 *
 * Matches the PostHog consent pattern (see updatePostHogConsent in
 * CookieConsent.tsx). vanilla-cookieconsent fires the callback whenever the
 * user's analytics-services list changes; we lazy-init Faro the first time
 * the flag flips to true. There is no faro.shutdown() upstream API, so a
 * later consent revoke would require a page reload to fully tear down.
 */
if (typeof window !== 'undefined') {
  window.updateFaroConsent = (granted: boolean) => {
    // Fire-and-forget: startFaro is async (dynamic import + initializeFaro)
    // but the consent callback contract is synchronous. The `void` makes the
    // intentional ignore explicit for linters / readers.
    if (granted) void startFaro();
  };
}

export default function GrafanaObservability(): null {
  return null;
}
